import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from '../../../shared/i18n';
import { BackgroundWrapper, PageContainer, PageTransition } from './';
import howToBuyBtn from '../../../assets/conectados/como-comprar-btn.webp';
import {
  AccessContainer,
  AccessCard,
  Title,
  Message,
  Input,
  Button,
  ErrorMessage,
  SuccessMessage,
  InfoBox,
  InfoText,
  SecondaryButton,
} from './AccessControl.styles';

// Access codes
const MANUAL_ACCESS_CODE = 'EC0409'; // Code printed in the game box for manual entry
const QR_ACCESS_CODES = ['CONECTADOS-V1.0']; // Codes that can be used via QR/URL parameter
const ACCESS_KEY = 'conectados_access';

// Helper function to validate access codes
const isValidAccessCode = (code, isFromUrl = false) => {
  const trimmedCode = code.trim().toUpperCase();
  
  if (isFromUrl) {
    // From URL parameter (QR code) - check QR codes
    return QR_ACCESS_CODES.some(validCode => validCode.toUpperCase() === trimmedCode);
  } else {
    // From manual input - check manual code
    return trimmedCode === MANUAL_ACCESS_CODE.toUpperCase();
  }
};


function AccessControl({ children, onAccessGranted }) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [accessCode, setAccessCode] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [hasAccess, setHasAccess] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const t = useTranslation();

  useEffect(() => {
    // Add meta tag to prevent search engine indexing
    const metaRobots = document.createElement('meta');
    metaRobots.name = 'robots';
    metaRobots.content = 'noindex,nofollow';
    document.head.appendChild(metaRobots);

    // Check for access code in URL parameter
    const urlAccessCode = searchParams.get('access');
    
    // Check for stored access in localStorage
    const storedAccess = localStorage.getItem(ACCESS_KEY);

    if (urlAccessCode) {
      // Validate QR/URL access code
      if (isValidAccessCode(urlAccessCode, true)) {
        // Store the access in localStorage (we just store a flag, not the code itself)
        localStorage.setItem(ACCESS_KEY, 'granted');
        setHasAccess(true);
        setSuccess(t('conectados.access.success'));
        
        // Clean the URL by removing the access parameter
        // This prevents the code from being visible in the address bar or browser history
        navigate(location.pathname, { replace: true });
        
        if (onAccessGranted) {
          onAccessGranted();
        }
      } else {
        setError(t('conectados.access.invalidCode'));
      }
      setIsChecking(false);
    } else if (storedAccess === 'granted') {
      // User has previously entered a valid code
      setHasAccess(true);
      setIsChecking(false);
      if (onAccessGranted) {
        onAccessGranted();
      }
    } else {
      setIsChecking(false);
    }

    // Cleanup: remove the meta tag when component unmounts
    return () => {
      const existingMeta = document.querySelector('meta[name="robots"]');
      if (existingMeta) {
        existingMeta.remove();
      }
    };
  }, [searchParams, navigate, location.pathname, t, onAccessGranted]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!accessCode.trim()) {
      setError(t('conectados.access.emptyCode'));
      return;
    }

    // Validate manual access code
    if (isValidAccessCode(accessCode, false)) {
      // Store the access in localStorage (we just store a flag, not the code itself)
      localStorage.setItem(ACCESS_KEY, 'granted');
      setHasAccess(true);
      setSuccess(t('conectados.access.success'));
      if (onAccessGranted) {
        onAccessGranted();
      }
    } else {
      setError(t('conectados.access.invalidCode'));
    }
  };

  const handleInputChange = (e) => {
    setAccessCode(e.target.value);
    setError('');
    setSuccess('');
  };

  // If checking, show loading
  if (isChecking) {
    return (
      <BackgroundWrapper>
        <PageContainer>
          <AccessContainer>
            <AccessCard>
              <Title>{t('common.loading')}</Title>
            </AccessCard>
          </AccessContainer>
        </PageContainer>
      </BackgroundWrapper>
    );
  }

  // If user has access, render the children (game content)
  if (hasAccess) {
    return children;
  }

  // Otherwise, show the access control form
  return (
    <BackgroundWrapper>
      <PageContainer>
        <PageTransition>
          <AccessContainer>
            <AccessCard>
              <Title>{t('conectados.access.title')}</Title>
              <Message>{t('conectados.access.message')}</Message>
              
              <form onSubmit={handleSubmit}>
                <Input
                  type="text"
                  value={accessCode}
                  onChange={handleInputChange}
                  placeholder={t('conectados.access.placeholder')}
                  autoFocus
                />
                {error && <ErrorMessage>{error}</ErrorMessage>}
                {success && <SuccessMessage>{success}</SuccessMessage>}
                <Button type="submit" disabled={!accessCode.trim()}>
                  {t('conectados.access.submit')}
                </Button>
              </form>

              <InfoBox>
                <InfoText>
                  <strong>{t('conectados.access.infoTitle')}</strong>
                </InfoText>
                <InfoText>{t('conectados.access.infoText')}</InfoText>
              </InfoBox>
            </AccessCard>

            <SecondaryButton onClick={() => navigate('/conectados/producto')}>
              <img src={howToBuyBtn} alt="Cómo comprar Conecta2" />
            </SecondaryButton>
          </AccessContainer>
        </PageTransition>
      </PageContainer>
    </BackgroundWrapper>
  );
}

AccessControl.propTypes = {
  children: PropTypes.node.isRequired,
  onAccessGranted: PropTypes.func,
};

export default AccessControl;

